import { useState, useCallback, useEffect, useLayoutEffect } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { scrollWindowToTop } from './ScrollToTop';
import SignupGuidedFrame from './SignupGuidedFrame';
import { isGuidedSignupPath, usesGuidedCopy } from '../utils/signupUxAb';
import '../styles/route-transition.css';

// Fallback: animação dura 0,7s; se `animationend` não disparar até aqui
// (aba ocluída/throttled, animação interrompida pelo browser), forçamos a
// conclusão da fase — sem isso a tela fica presa em opacity: 0 (fill "both").
const FADE_FALLBACK_MS = 900;

const ROUTES_WITHOUT_FADE = new Set([
  '/consulta/chat',
  '/consulta/chat/pago',
]);

/** Home: fade-in expõe decode progressivo do poster do mascote (vídeo). */
const ROUTES_WITHOUT_FADE_IN = new Set(['/']);

export function shouldAnimateRoute(pathname) {
  if (ROUTES_WITHOUT_FADE.has(pathname)) return false;
  if (usesGuidedCopy() && isGuidedSignupPath(pathname)) return false;
  return true;
}

export function shouldFadeInRoute(pathname) {
  return shouldAnimateRoute(pathname) && !ROUTES_WITHOUT_FADE_IN.has(pathname);
}

const TITLE_FADE_FALLBACK_MS = 480;

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Braço guided: só o título do card some; o fundo e o modal ficam. */
export function shouldTitleFadeRoute(fromPath, toPath) {
  if (prefersReducedMotion()) return false;
  return (
    usesGuidedCopy()
    && isGuidedSignupPath(fromPath)
    && isGuidedSignupPath(toPath)
  );
}

/**
 * Outlet do fluxo paciente com fade-out (0,7s) + fade-in (0,7s) entre telas.
 * Chat e rotas sem animação trocam conteúdo na hora.
 *
 * A troca de tela é detectada DURANTE o render (não num useEffect). Isso evita
 * o frame intermediário em que a tela nova já aparecia antes do fade-out — esse
 * frame desmontava/remontava a Home e fazia o vídeo do mascote reiniciar (ou
 * sortear outro vídeo), dando a impressão de "vídeo interrompido". Mantendo o
 * mesmo elemento de outlet visível durante o fade-out, o vídeo segue tocando.
 */
export default function AnimatedOutlet() {
  const location = useLocation();
  const currentOutlet = useOutlet();
  const currentPath = location.pathname;

  // displayed: outlet atualmente visível (estável durante o fade-out).
  // pending: outlet aguardando para entrar com fade-in (enquanto phase === 'out').
  const [render, setRender] = useState({
    displayed: currentOutlet,
    displayedPath: currentPath,
    pending: null,
    pendingPath: null,
    phase: null, // null | 'out' | 'in'
  });

  // Detecta navegação no próprio render: nunca pintamos a rota nova antes de
  // decidir o fade-out da rota atual.
  if (currentPath !== render.displayedPath && currentPath !== render.pendingPath) {
    const prevAnimated = shouldAnimateRoute(render.displayedPath);
    const nextAnimated = shouldAnimateRoute(currentPath);
    const titleFade = shouldTitleFadeRoute(render.displayedPath, currentPath);
    const skipFade =
      !titleFade && (
        (usesGuidedCopy() && isGuidedSignupPath(currentPath)) ||
        (usesGuidedCopy() && isGuidedSignupPath(render.displayedPath))
      );

    if (titleFade) {
      setRender((r) => ({
        ...r,
        pending: currentOutlet,
        pendingPath: currentPath,
        phase: 'title-out',
      }));
    } else if (prevAnimated && !skipFade) {
      // Faz a tela atual sair com fade; a troca acontece ao fim da animação.
      setRender((r) => ({
        ...r,
        pending: currentOutlet,
        pendingPath: currentPath,
        phase: 'out',
      }));
    } else {
      // Saindo de rota sem animação (ex.: chat): troca na hora, com fade-in
      // apenas quando a rota de destino pede.
      setRender({
        displayed: currentOutlet,
        displayedPath: currentPath,
        pending: null,
        pendingPath: null,
        phase: nextAnimated && shouldFadeInRoute(currentPath) ? 'in' : null,
      });
    }
  }

  // Após o fade-out a página nova monta herdando o scrollY da anterior
  // (Home rolada → /consulta|/reviews|/diretor no meio). Reset no momento
  // em que o conteúdo exibido troca — cobre o delay do AnimatedOutlet,
  // além do ScrollToTop global (que dispara no pathname).
  useLayoutEffect(() => {
    scrollWindowToTop();
  }, [render.displayedPath]);

  // Watchdog da transição: se a fase atual não terminar via animationend
  // dentro do fallback, aplica o mesmo avanço de estado que o handler faria.
  useEffect(() => {
    if (!render.phase) return undefined;
    const ms = render.phase === 'title-out' || render.phase === 'title-in'
      ? TITLE_FADE_FALLBACK_MS
      : FADE_FALLBACK_MS;
    const timer = setTimeout(() => {
      setRender((r) => {
        if (r.phase === 'out' || r.phase === 'title-out') {
          return {
            displayed: r.pending,
            displayedPath: r.pendingPath,
            pending: null,
            pendingPath: null,
            phase: r.phase === 'title-out'
              ? 'title-in'
              : (shouldFadeInRoute(r.pendingPath) ? 'in' : null),
          };
        }
        if (r.phase === 'in' || r.phase === 'title-in') return { ...r, phase: null };
        return r;
      });
    }, ms);
    return () => clearTimeout(timer);
  }, [render.phase]);

  const handleAnimationEnd = useCallback(
    (e) => {
      const { animationName } = e;
      const fromChild = e.target !== e.currentTarget;
      if (fromChild && animationName !== 'sgf-title-out' && animationName !== 'sgf-title-in') return;
      if (!fromChild && animationName !== 'route-fade-out' && animationName !== 'route-fade-in') return;

      if (animationName === 'route-fade-out' || animationName === 'sgf-title-out') {
        setRender((r) => {
          if (r.phase !== 'out' && r.phase !== 'title-out') return r;
          return {
            displayed: r.pending,
            displayedPath: r.pendingPath,
            pending: null,
            pendingPath: null,
            phase: r.phase === 'title-out'
              ? 'title-in'
              : (shouldFadeInRoute(r.pendingPath) ? 'in' : null),
          };
        });
        return;
      }

      if (animationName === 'route-fade-in' || animationName === 'sgf-title-in') {
        setRender((r) => (
          r.phase === 'in' || r.phase === 'title-in' ? { ...r, phase: null } : r
        ));
      }
    },
    []
  );

  const pageClass =
    render.phase === 'out'
      ? 'route-fade-page--out'
      : render.phase === 'in'
        ? 'route-fade-page--in'
        : render.phase === 'title-out'
          ? 'route-fade-page--title-out'
          : render.phase === 'title-in'
            ? 'route-fade-page--title-in'
            : undefined;

  const guidedChrome =
    usesGuidedCopy() && isGuidedSignupPath(render.displayedPath);

  return (
    <div className={`route-fade-root${guidedChrome ? ' route-fade-root--sgf' : ''}`}>
      {guidedChrome ? <SignupGuidedFrame pathname={render.displayedPath} /> : null}
      <div className={pageClass} onAnimationEnd={handleAnimationEnd}>
        {render.displayed}
      </div>
    </div>
  );
}
